import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, Tooltip } from "@mui/material";
import ExpandableTable, {
  BasicCellType,
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/ExpandableTable";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import {
  AdminUserSkuRequest,
  IUserSkuExtended,
  UserChargebackFilter,
  UserSkuFilter,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { ProductTypeIds } from "types/common-types";
import { convertToUTC, convertToZonedDate } from "utils/dateUtils";
import UserSkusTableTitle from "./UserSkusTableTitle";
import { UserSkuTypeCell } from "./UserSkuTypeCell";

type UserSkusTableProps = {
  userId: string;
  organizationId: string;
  parentUserSkuId?: string;
  title?: string;
  onEmptyChildSkus?: () => void;
  cells?: Array<TableCellType>;
  completedDateFrom?: Date;
};

interface IUserSkuRow extends IUserSkuExtended, LazyTableRow {
  id: string;
}

export const UserSkusTable = ({
  userId,
  organizationId,
  parentUserSkuId,
  title,
  cells,
  onEmptyChildSkus,
}: UserSkusTableProps) => {
  const { formatMessage: t } = useIntl();
  const { userSkuService, userCourseUrl } = useGlobalContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("");

  const searchTerm = persistanceStore.useState(
    (s) => s.userSkusFilterSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const startDateSearch = persistanceStore.useState(
    (s) => s.userSkusFilterStartDate
  );

  const endDateSearch = persistanceStore.useState(
    (s) => s.userSkusFilterEndDate
  );

  const chargebackRenewalDate = persistanceStore.useState(
    (s) => s.userSkusFilterChargebackRenewalDate
  );

  const skuFormatIds = persistanceStore.useState(
    (s) => s.userSkusFilterSkuFormatIds
  );

  const userSkuStateIds = persistanceStore.useState(
    (s) => s.userSkusFilterStateIds
  );

  const orderedProductTypeIds = [
    ProductTypeIds.Membership,
    ProductTypeIds.Package,
  ];

  const request = AdminUserSkuRequest.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parentUserSkuId,
    userId,
    organizationId,
    parameters: UserSkuFilter.fromJS({
      searchText: debouncedSearchTerm === "" ? undefined : debouncedSearchTerm,
      startDate: startDateSearch ? convertToUTC(startDateSearch) : undefined,
      endDate: endDateSearch ? convertToUTC(endDateSearch) : undefined,
      userChargebackFilter: chargebackRenewalDate
        ? new UserChargebackFilter({
            renewalDate: convertToUTC(chargebackRenewalDate),
          })
        : undefined,
      includedSkuFormatIds: skuFormatIds.length ? skuFormatIds : undefined,
      userSkuStateIds: userSkuStateIds.length ? userSkuStateIds : undefined,
      orderedProductTypeIds: orderedProductTypeIds,
    }),
  });

  const isChildTable = !!parentUserSkuId;

  const { data, isFetching } = useStrictQuery(
    [
      "userSkus",
      userId,
      parentUserSkuId,
      chargebackRenewalDate,
      startDateSearch,
      endDateSearch,
      skuFormatIds,
      userSkuStateIds,
      page,
      rowsPerPage,
      order,
      orderBy,
      debouncedSearchTerm,
    ],
    () => userSkuService.getUserSkus(request),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearchTerm,
    startDateSearch,
    endDateSearch,
    skuFormatIds,
    userSkuStateIds,
  ]);

  const ManageAction = ({ value: userSku }: { value: IUserSkuExtended }) => {
    return (
      <Tooltip title={t({ id: "manage" })}>
        <Link to={userCourseUrl(userSku.userId, userSku.id)}>
          <IconButton size="small" style={{ color: "darkblue" }}>
            <MoreHorizIcon />
          </IconButton>
        </Link>
      </Tooltip>
    );
  };

  const basicCell = {
    align: "left",
    width: 0,
    type: "text",
  } as BasicCellType;

  const defaultCells: Array<TableCellType> = [
    {
      ...basicCell,
      accessor: "sku",
      label: t({ id: "skuLabel" }),
    },
    {
      ...basicCell,
      accessor: "title",
      label: t({ id: "skuTitleLabel" }),
    },
    {
      ...basicCell,
      accessor: "format",
      label: t({ id: "formatLabel" }),
    },
    {
      ...basicCell,
      accessor: "startDate",
      type: "date",
      label: t({ id: "startDateLabel" }),
    },
    {
      ...basicCell,
      accessor: "endDate",
      type: "date",
      label: t({ id: "endDateLabel" }),
    },
    {
      ...basicCell,
      accessor: "userSkuStateName",
      customSortAccessor: "userSkuState.name",
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "",
      align: "left",
      width: 0,
      type: "node",
      label: t({ id: "type" }),
      View: UserSkuTypeCell,
      noSort: true,
    },
    {
      accessor: "",
      align: "center",
      width: 0,
      type: "node",
      label: t({ id: "manageLabel" }),
      View: ManageAction,
      noSort: true,
    },
  ];

  const userSkus = (data?.data ?? []).map(
    (us) =>
      ({
        ...us,
        startDate: convertToZonedDate(us?.startDate),
        endDate: convertToZonedDate(us?.endDate),
        isExpandable: us.hasChildren ?? false,
        renderExpandedData:
          us.hasChildren &&
          (() => (
            <UserSkusTableWithErrorBoundary
              {...{
                userId,
                parentUserSkuId: us.id,
                organizationId,
              }}
            />
          )),
      } as IUserSkuRow)
  );

  useEffect(() => {
    if (
      isChildTable &&
      !isFetching &&
      userSkus.length === 0 &&
      onEmptyChildSkus
    ) {
      onEmptyChildSkus();
    }
  }, [isChildTable, onEmptyChildSkus, userSkus, isFetching]);

  return (
    <ExpandableTable
      {...{
        rowCount: data?.totalRecords ?? 0,
        rows: userSkus,
        loading: isFetching,
        dense: true,
        cells: cells ?? defaultCells,
        page,
        setPage,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        rowsPerPage,
        setRowsPerPage,
        title: title
          ? title
          : isChildTable
          ? t({ id: "includedSkusTitle" })
          : undefined,
        titleComponent: isChildTable ? undefined : (
          <UserSkusTableTitle userId={userId} />
        ),
        collapsable: userSkus.some((us) => us.isExpandable),
      }}
    />
  );
};

const UserSkusTableWithErrorBoundary = withErrorBoundary(UserSkusTable);

export default UserSkusTableWithErrorBoundary;
